import { createContext, useContext } from 'react';

export type ColorMode = 'light' | 'dark' | 'system';
export type ResolvedColorMode = 'light' | 'dark';

export interface ColorModeContextValue {
  /** What the user chose, which may be `system`. */
  mode: ColorMode;
  /** What `mode` currently resolves to — the value the theme and the logo are picked by. */
  resolvedMode: ResolvedColorMode;
  setMode: (mode: ColorMode) => void;
  cycleMode: () => void;
}

export const ColorModeContext = createContext<ColorModeContextValue | null>(null);

export function useColorMode(): ColorModeContextValue {
  const value = useContext(ColorModeContext);
  if (!value) throw new Error('useColorMode must be used inside a ColorModeProvider.');
  return value;
}

export const colorModeStorageKey = 'algobridge.colorMode';

export function readStoredColorMode(): ColorMode {
  try {
    const stored = localStorage.getItem(colorModeStorageKey);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    // Storage can be unavailable (private browsing, blocked cookies); the default still works.
    return 'system';
  }
}

export function storeColorMode(mode: ColorMode): void {
  try {
    localStorage.setItem(colorModeStorageKey, mode);
  } catch {
    // Losing the preference across reloads is better than failing the toggle.
  }
}

/** Translation keys rather than text, so the toggle's label follows the language like everything else. */
export const colorModeLabelKeys = {
  light: 'colorMode.light',
  dark: 'colorMode.dark',
  system: 'colorMode.system',
} as const satisfies Record<ColorMode, string>;

/** light → dark → system → light. */
export const nextColorMode: Record<ColorMode, ColorMode> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};
